import { FormField, FormResponseValue } from "@tellescope/types-models";
import { resolveFieldOptionDetails } from "./resolve-field-option-details";
import { resolveVisibleChoices } from "./resolve-visible-choices";
import { NextFieldLogicOptions } from "./types";

type ResponseMap = Record<string, FormResponseValue>;

export type StaleChoiceCandidateField = Pick<FormField, "id" | "type" | "options">;

export interface DropStaleChoiceSelectionsArgs {
  responses: ResponseMap;
  fieldsArr: StaleChoiceCandidateField[];
  changedFieldId?: string;
  logicOptions?: NextFieldLogicOptions;
}

export interface DropStaleChoiceSelectionsResult {
  responses: ResponseMap;
  emptiedFieldIds: Set<string>;
}

/**
 * Removes choice selections whose options are no longer visible, because an answer elsewhere flipped
 * an option-level `showCondition`. Without it a hidden option stays selected and passes validation,
 * since the stored array is still non-empty.
 *
 * Free-text "other" values are not choices and always survive.
 */
export const dropStaleChoiceSelections = ({
  responses,
  fieldsArr,
  changedFieldId,
  logicOptions,
}: DropStaleChoiceSelectionsArgs): DropStaleChoiceSelectionsResult => {
  const responsesArr = Object.values(responses);

  return fieldsArr.reduce<DropStaleChoiceSelectionsResult>(
    (acc, field) => {
      if (field.id === changedFieldId) return acc;
      if (field.type !== "multiple_choice" && field.type !== "Dropdown") return acc;

      const response = acc.responses[field.id];
      if (response?.answer.type !== "multiple_choice" && response?.answer.type !== "Dropdown") return acc;
      const selected = response.answer.value;
      if (!Array.isArray(selected) || !selected.length) return acc;

      const optionDetails = resolveFieldOptionDetails(field.options);
      if (!optionDetails.some((option) => option.showCondition)) return acc;

      const visible = resolveVisibleChoices({ optionDetails, responses: responsesArr, logicOptions }).map(
        (option) => option.option
      );
      const allChoices = optionDetails.map((option) => option.option);
      const kept = selected.filter((v) => !allChoices.includes(v) || visible.includes(v));
      if (kept.length === selected.length) return acc;

      const updated: FormResponseValue = { ...response, answer: { ...response.answer, value: kept } };
      return {
        responses: { ...acc.responses, [field.id]: updated },
        emptiedFieldIds: kept.length ? acc.emptiedFieldIds : new Set([...acc.emptiedFieldIds, field.id]),
      };
    },
    { responses, emptiedFieldIds: new Set() }
  );
};
